import { useState, useEffect, useCallback } from 'react'
import {
    Page, Layout, Card, FormLayout, TextField, Select, Button,
    Banner, BlockStack, Text, Spinner, InlineStack, Checkbox,
} from '@shopify/polaris'
import { apiGet, apiPost } from '../lib/api'

interface TenantConfig {
    shop: string
    label: string
    apiUrl: string
    apiKey: string
    profileId: string
    publishableKey: string
    accountName: string
    dueDateDays: number
    autoAuthorize: boolean
    paymentResponseHashKey: string
}

const API_URL_OPTIONS = [
    { label: 'Sandbox (api.sandbox.bnplx.io)', value: 'https://api.sandbox.bnplx.io' },
    { label: 'Production (api.bnplx.io)', value: 'https://api.bnplx.io' },
]

const EMPTY_CONFIG: TenantConfig = {
    shop: '',
    label: '',
    apiUrl: 'https://api.sandbox.bnplx.io',
    apiKey: '',
    profileId: '',
    publishableKey: '',
    accountName: 'transaction_processing',
    dueDateDays: 30,
    autoAuthorize: false,
    paymentResponseHashKey: '',
}

export default function Settings() {
    const [config, setConfig] = useState<TenantConfig>({ ...EMPTY_CONFIG })
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<string | null>(null)
    const [configured, setConfigured] = useState(false)

    // Load current config for this shop
    useEffect(() => {
        setLoading(true)
        apiGet<{ shop: string; config: TenantConfig | null }>('/api/settings')
            .then(data => {
                if (data.config) {
                    setConfig(data.config)
                    setConfigured(true)
                } else {
                    setConfig(prev => ({ ...prev, shop: data.shop }))
                }
            })
            .catch(err => setError(err.message || 'Failed to load settings'))
            .finally(() => setLoading(false))
    }, [])

    const handleSave = useCallback(async () => {
        setError(null)
        setSuccess(null)

        if (!config.apiKey || !config.profileId || !config.publishableKey) {
            setError('API Key, Profile ID, and Publishable Key are required.')
            return
        }

        setSaving(true)
        try {
            await apiPost('/api/settings', {
                apiUrl: config.apiUrl,
                apiKey: config.apiKey,
                profileId: config.profileId,
                publishableKey: config.publishableKey,
                accountName: config.accountName || 'transaction_processing',
                dueDateDays: Number(config.dueDateDays) || 30,
                autoAuthorize: config.autoAuthorize,
                paymentResponseHashKey: config.paymentResponseHashKey || '',
            })
            setSuccess('Settings saved successfully.')
            setConfigured(true)
        } catch (err: any) {
            setError(err.message || 'Failed to save settings')
        } finally {
            setSaving(false)
        }
    }, [config])

    const updateField = useCallback((field: keyof TenantConfig) => (value: string) => {
        setConfig(prev => ({ ...prev, [field]: value }))
    }, [])

    if (loading) {
        return (
            <Page title="Settings" backAction={{ content: 'Draft Orders', url: '/' }}>
                <Card>
                    <InlineStack align="center" gap="300">
                        <Spinner size="small" />
                        <Text as="span" tone="subdued">Loading settings...</Text>
                    </InlineStack>
                </Card>
            </Page>
        )
    }

    return (
        <Page title="Settings" backAction={{ content: 'Draft Orders', url: '/' }}>
            <Layout>
                {(error || success) && (
                    <Layout.Section>
                        <BlockStack gap="300">
                            {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
                            {success && <Banner tone="success" onDismiss={() => setSuccess(null)}>{success}</Banner>}
                        </BlockStack>
                    </Layout.Section>
                )}

                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <Text as="h2" variant="headingMd">BNPLX Configuration</Text>
                            <FormLayout>
                                <Select
                                    label="Environment"
                                    options={API_URL_OPTIONS}
                                    value={config.apiUrl}
                                    onChange={updateField('apiUrl')}
                                    helpText="Use Sandbox for testing, Production for live payments"
                                />
                                <TextField
                                    label="API Key"
                                    value={config.apiKey}
                                    onChange={updateField('apiKey')}
                                    placeholder="snd_... or live_..."
                                    requiredIndicator
                                    autoComplete="off"
                                    helpText="Your BNPLX server API key"
                                />
                                <TextField
                                    label="Profile ID"
                                    value={config.profileId}
                                    onChange={updateField('profileId')}
                                    placeholder="pro_..."
                                    requiredIndicator
                                    autoComplete="off"
                                    helpText="Your BNPLX payment profile"
                                />
                                <TextField
                                    label="Publishable Key"
                                    value={config.publishableKey}
                                    onChange={updateField('publishableKey')}
                                    placeholder="pk_..."
                                    requiredIndicator
                                    autoComplete="off"
                                    helpText="Used for customer hash generation"
                                />
                                <TextField
                                    label="Payment Response Hash Key"
                                    value={config.paymentResponseHashKey}
                                    onChange={updateField('paymentResponseHashKey')}
                                    placeholder="e.g. a1b2c3…"
                                    autoComplete="off"
                                    helpText="Used to verify incoming BNPLX webhooks"
                                />
                                <FormLayout.Group>
                                    <TextField
                                        label="Account Name"
                                        value={config.accountName}
                                        onChange={updateField('accountName')}
                                        autoComplete="off"
                                        helpText="Default: transaction_processing"
                                    />
                                    <TextField
                                        label="Payment Due Days"
                                        type="number"
                                        value={String(config.dueDateDays)}
                                        onChange={(v) => setConfig(prev => ({ ...prev, dueDateDays: parseInt(v, 10) || 30 }))}
                                        autoComplete="off"
                                        helpText="Default: 30 days"
                                    />
                                </FormLayout.Group>
                                <Checkbox
                                    label="Automatic authorization"
                                    checked={config.autoAuthorize}
                                    onChange={(v) => setConfig(prev => ({ ...prev, autoAuthorize: v }))}
                                    helpText="When on, guarantees are authorized automatically once a draft has complete B2B data."
                                />
                            </FormLayout>
                            <InlineStack>
                                <Button
                                    variant="primary"
                                    onClick={handleSave}
                                    loading={saving}
                                >
                                    {configured ? 'Update Settings' : 'Save Settings'}
                                </Button>
                            </InlineStack>
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    )
}
